import { useContext, forwardRef } from 'react';
import { Link, NavLink, withRouter } from 'react-router-dom';

// Externals
import classNames from 'classnames';

// Authentication provider & Firebase
import { AuthContext } from 'services/auth';

// Shared helpers
import { getInitials } from 'helpers';

// Material components
import {
  // Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  // CircularProgress,
  Chip,
  withStyles
} from '@material-ui/core';

// Info icon
import {
  InfoRounded as InfoIcon,
  BugReportRounded as BugIcon,
  QuestionAnswerRounded as FAQ
} from '@mui/icons-material';

// Material UI lab
import { Skeleton } from '@material-ui/lab';

// Sidebar items
import getSidebarItems from 'services/views';

// Cleanup exports
import compose from 'recompose/compose';

// App config
import AppConfig from 'config';

// Component styles
import styles from './styles';
import { DateTime } from 'luxon';

// SUBSCRIPTION CONFIGURATION MAP
// Maps Price IDs to display details for legacy/fallback support
const PLAN_DETAILS = {
  // Workplace English
  price_1SptkrHns6zXPgc8mJODldvz: {
    title: 'Workplace English',
    tokenName: 'workplaceLessonTokens',
    label: 'One-on-One Lessons'
  },
  price_1SpvGYHns6zXPgc8DKMCfFWp: {
    title: 'Workplace English',
    tokenName: 'workplaceGroupLessonTokens',
    label: 'Group Sessions'
  },

  // PTE English
  price_1RyWeAHns6zXPgc8zM6LN9LF: {
    title: 'PTE English',
    tokenName: 'PTELessonTokens',
    label: 'One-on-One Lessons'
  },
  price_1Sg0VXHns6zXPgc8UdS7MI03: {
    title: 'PTE English',
    tokenName: 'PTELessonTokens',
    label: 'One-on-One Lessons'
  },
  price_1Sg0wAHns6zXPgc80aq9855D: {
    title: 'PTE English',
    tokenName: 'PTEGroupLessonTokens',
    label: 'Group Sessions'
  },
  price_1S7sUgHns6zXPgc861vlNgvc: {
    title: 'PTE English',
    tokenName: 'PTEGroupLessonTokens',
    label: 'Group Sessions'
  },

  // PTE A2/B2 Variants
  price_1R4HRwHns6zXPgc8A2KYJiq8: {
    title: 'PTE English A2',
    tokenName: 'A2PTELessonTokens',
    label: 'One-on-One Lessons'
  },
  price_1R4HKZHns6zXPgc866zuYJzK: {
    title: 'PTE English B2',
    tokenName: 'B2PTELessonTokens',
    label: 'One-on-One Lessons'
  },
  price_1R4He5Hns6zXPgc8fCOkH7Mt: {
    title: 'PTE English B2',
    tokenName: 'B2PTEGroupLessonTokens',
    label: 'Group Sessions'
  },
  price_1R4IerHns6zXPgc8RBjX0DfX: {
    title: 'PTE English A2', // json says PTE English, but logic suggests A2
    tokenName: 'A2PTEGroupLessonTokens',
    label: 'Group Sessions'
  }
};

function Sidebar(props) {
  // Grab values out of the component's properties
  const {
    classes,
    className,
    itemClicked,
    profile,
    profileError // ,
    // onProfileRetry
  } = props;

  const auth = useContext(AuthContext);

  const { displayName, photoURL } = auth.user;

  // Get the root classname
  const rootClassName = classNames(classes.root, className);

  // Create a ref component for the sidebar links
  // eslint-disable-next-line react/display-name
  const SidebarNavLink = forwardRef((newProps, ref) => (
    <NavLink onClick={itemClicked} innerRef={ref} {...newProps} />
  ));

  return (
    <nav className={rootClassName}>
      <div className={classes.logoWrapper}>
        <Link className={classes.logoLink} to={`/${auth.role}/dashboard`}>
          <div className={classes.logoContent}>
            <img
              width="55px"
              height="55px"
              alt="e-English logo"
              className={classes.logoImage}
              src="/images/logos/new-logo.png"
            />
            <div className={classes.logoText}>
              <Typography variant="h3" className={classes.logoTitle}>
                e-English
              </Typography>
              <Typography variant="caption" className={classes.logoSubtitle}>
                English Online Tutoring
              </Typography>
            </div>
          </div>
        </Link>
      </div>
      <Divider className={classes.logoDivider} />
      <div className={classes.profile}>
        <Avatar alt={displayName} className={classes.avatar} src={photoURL}>
          <Typography variant="h3" style={{ color: 'white' }}>
            {getInitials(displayName && displayName)}
          </Typography>
        </Avatar>
        <div className={classes.profileDetails}>
          <Typography className={classes.nameText} variant="h5">
            {displayName}
          </Typography>
          <Typography className={classes.bioText} variant="caption">
            {
              {
                student: 'Student',
                tutor: 'Teacher',
                admin: 'Administrator'
              }[auth.role]
            }
          </Typography>
        </div>
      </div>
      <Divider className={classes.profileDivider} />
      {(() => {
        // Make sure we're a student
        if (auth.role === 'student') {
          if (profileError) {
            return (
              <div className={classes.tokenInfo}>
                <Typography variant="h6">{profileError}</Typography>
                <Divider className={classes.profileDivider} />
              </div>
            );
          }

          if (!profile) {
            return (
              <>
                <div className={classes.tokenInfo}>
                  <Skeleton className={classes.skeletonChip}>
                    <Chip size="small" label="10 Lesson Tokens" />
                  </Skeleton>
                  <Skeleton
                    className={`${classes.skeletonChip} ${classes.subBadge}`}>
                    <Chip size="small" label="No Subscription" />
                  </Skeleton>
                </div>
                <Divider className={classes.profileDivider} />
              </>
            );
          }

          if (profile) {
            const sub = profile.stripe.subscription;
            const freeTrialActivated = profile.stripe.freeTrialActivated;

            console.log('=== SIDEBAR SUBSCRIPTION ANALYSIS ===', {
              previousSubscriptions: sub?.currentSubscription?.categories || [],
              currentProfileTokens: {
                workplaceLessonTokens: profile.data?.workplaceLessonTokens || 0,
                workplaceGroupLessonTokens:
                  profile.data?.workplaceGroupLessonTokens || 0,
                PTELessonTokens: profile.data?.PTELessonTokens || 0,
                PTEGroupLessonTokens: profile.data?.PTEGroupLessonTokens || 0
              }
            });

            const categories = sub?.currentSubscription?.categories || [];

            // 1. Enrich categories with display details
            const enrichedCategories = categories.map((cat) => {
              const defaults = PLAN_DETAILS[cat.priceId];

              // Determine the label intelligently
              let label = 'Lessons';
              if (defaults?.label) {
                // Use PLAN_DETAILS if available
                label = defaults.label;
              } else if (cat.type) {
                // Check if the type indicates it's a group session
                const typeStr = cat.type.toLowerCase();
                if (typeStr.includes('group')) {
                  label = 'Group Sessions';
                } else if (
                  typeStr.includes('one on one') ||
                  typeStr.includes('one-on-one')
                ) {
                  label = 'One-on-One Lessons';
                } else {
                  label = cat.type;
                }
              }

              // Priority: Use PLAN_DETAILS mapping first, then fallback to database values
              const enriched = {
                ...cat,
                title: defaults?.title || cat.name || 'English Plan',
                tokenName:
                  defaults?.tokenName || cat.tokenName || 'lessonTokens',
                label: label
              };

              console.log('Enriching category:', {
                priceId: cat.priceId,
                originalName: cat.name,
                originalType: cat.type,
                originalTokenName: cat.tokenName,
                planDetailsFound: !!defaults,
                planDetails: defaults,
                enrichedTitle: enriched.title,
                enrichedLabel: enriched.label,
                enrichedTokenName: enriched.tokenName
              });

              return enriched;
            });

            // 2. Filter active
            const activeCategories = enrichedCategories.filter(
              (cat) =>
                cat.expiredAt > Date.now() ||
                (profile.data?.[cat.tokenName] || 0) > 0
            );

            // 3. Group by Title -> TokenName
            const grouped = {};
            activeCategories.forEach((cat) => {
              if (!grouped[cat.title]) grouped[cat.title] = {};
              if (!grouped[cat.title][cat.tokenName]) {
                grouped[cat.title][cat.tokenName] = [];
              }
              grouped[cat.title][cat.tokenName].push(cat);
            });

            const hasActiveSubscriptions = Object.keys(grouped).length > 0;

            return (
              <>
                <div className={classes.tokenInfo}>
                  {hasActiveSubscriptions ? (
                    Object.entries(grouped).map(([title, tokenGroups], idx) => (
                      <div
                        key={title}
                        className={classes.subscription}
                        style={idx > 0 ? { marginTop: '10px' } : {}}>
                        <div className={classes.subscriptionTitle}>
                          <Typography variant="h5">{title}</Typography>
                        </div>
                        {Object.entries(tokenGroups).map(
                          ([tokenName, groupParams], subIdx) => {
                            // Pick the latest subscription for this token type to display expiry
                            const latestSub = groupParams.sort(
                              (a, b) => (b.expiredAt || 0) - (a.expiredAt || 0)
                            )[0];

                            // Use global token count for this token type
                            const displayTokens = profile.data[tokenName] || 0;

                            const expiryDate = latestSub.expiredAt
                              ? DateTime.fromMillis(
                                  latestSub.expiredAt < 10000000000
                                    ? latestSub.expiredAt * 1000
                                    : latestSub.expiredAt
                                ).toFormat('dd LLLL yyyy')
                              : 'No expiry';

                            return (
                              <div key={tokenName}>
                                {subIdx > 0 && (
                                  <Divider className={classes.subdevider} />
                                )}
                                <Typography
                                  className={classes.subscription_name}
                                  variant="h6">
                                  {displayTokens} {latestSub.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary">
                                  Expires {expiryDate}
                                </Typography>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ))
                  ) : freeTrialActivated &&
                    (profile.data?.freeTrialToken || 0) > 0 ? (
                    <div className={classes.subscription}>
                      <div className={classes.subscriptionTitle}>
                        <Typography variant="h5">Free Trial</Typography>
                      </div>
                      <Typography
                        className={classes.subscription_name}
                        variant="h6">
                        {profile.data?.freeTrialToken || 0} Free Trial Lessons
                      </Typography>
                    </div>
                  ) : (
                    <Chip
                      size="small"
                      className={`${classes.infoChip}`}
                      label="No Subscription"
                    />
                  )}
                </div>
                <Divider className={classes.profileDivider} />
              </>
            );
          }
        }

        return null;
      })()}
      <List component="div" disablePadding>
        {getSidebarItems(auth.role, auth.isDevBuild).map((item) => {
          if (item.icon !== undefined && item.text !== undefined) {
            return (
              <ListItem
                activeClassName={classes.activeListItem}
                className={classes.listItem}
                component={SidebarNavLink}
                to={item.route}
                key={item.route}>
                <ListItemIcon className={classes.listItemIcon}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  classes={{ primary: classes.listItemText }}
                  primary={item.text}
                />
              </ListItem>
            );
          }
          return null;
        })}
      </List>
      <Divider className={classes.listDivider} />
      <List
        component="div"
        disablePadding
        subheader={
          <ListSubheader className={classes.listSubheader}>
            Support
          </ListSubheader>
        }>
        <ListItem
          className={classes.listItem}
          component="a"
          href="mailto:support@e-english.com.au"
          target="_blank">
          <ListItemIcon className={classes.listItemIcon}>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText
            classes={{ primary: classes.listItemText }}
            primary="Contact Us"
          />
        </ListItem>
        {auth.role !== 'admin' && (
          <ListItem
            className={classes.listItem}
            component="a"
            href={
              auth.role === 'tutor'
                ? 'https://docs.google.com/forms/d/e/1FAIpQLSc1abmrnw9R9l4-QqAUynwxwlPi2CCKhJHQjliy3luOoCZbsA/viewform?usp=sharing'
                : 'https://docs.google.com/forms/d/e/1FAIpQLSextGBWDBn1kgZWaKXG88eskrDceITjK5ryqbqCl2QNTjtTUQ/viewform?usp=sharing'
            }
            target="_blank">
            <ListItemIcon className={classes.listItemIcon}>
              <BugIcon />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.listItemText }}
              primary="Submit feedback"
            />
          </ListItem>
        )}
        {auth.role == 'tutor' && (
          <ListItem
            className={classes.listItem}
            component="a"
            href={
              'https://firebasestorage.googleapis.com/v0/b/eng-tutor-app-dev.appspot.com/o/tutorfaqs%2FFAQ%20TUTOR_v1.pdf?alt=media&token=5e4ee3bf-5566-4f0f-9600-8ff461b600df'
            }
            target="_blank">
            <ListItemIcon className={classes.listItemIcon}>
              <FAQ />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.listItemText }}
              primary="FAQ"
            />
          </ListItem>
        )}
      </List>
      <Divider className={classes.listDivider} />
      <ListSubheader className={classes.listVersionSubheader}>
        Build ID: {AppConfig.buildId}
      </ListSubheader>
    </nav>
  );
}

export default compose(withRouter, withStyles(styles))(Sidebar);
